// Dues plans, enrolments, charges and payments. Money is ALWAYS integer ngwee
// here; it is formatted to Kwacha only at the very edge (formatKwacha).
import { supabase } from '@/lib/supabase'
import { FriendlyError, currentUserId } from './auth'
import { DataError, unwrap } from './errors'

export type Cadence = 'monthly' | 'quarterly'
export type PaymentMethod = 'cash' | 'mobile_money' | 'bank_transfer'

export const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  mobile_money: 'Mobile money',
  bank_transfer: 'Bank transfer',
}

// ---------------------------------------------------------------- plans
export interface DuesPlan {
  id: string
  name: string
  cadence: Cadence
  amountNgwee: number
  dueDay: number
  effectiveFrom: string
}

type PlanRow = {
  id: string
  name: string
  cadence: Cadence
  amount_ngwee: number
  due_day: number
  effective_from: string
}

export async function listPlans(departmentId: string): Promise<DuesPlan[]> {
  const res = await supabase
    .from('dues_plan')
    .select('id, name, cadence, amount_ngwee, due_day, effective_from')
    .eq('department_id', departmentId)
    .is('archived_at', null)
    .order('name')
  return (unwrap(res as never, 'Could not load the dues plans.') as PlanRow[]).map((p) => ({
    id: p.id,
    name: p.name,
    cadence: p.cadence,
    amountNgwee: Number(p.amount_ngwee),
    dueDay: p.due_day,
    effectiveFrom: p.effective_from,
  }))
}

export interface NewPlan {
  departmentId: string
  name: string
  cadence: Cadence
  amountNgwee: number
  dueDay: number
  effectiveFrom: string
}

export async function createPlan(p: NewPlan): Promise<void> {
  const userId = await currentUserId()
  const res = await supabase.from('dues_plan').insert({
    department_id: p.departmentId,
    name: p.name.trim(),
    cadence: p.cadence,
    amount_ngwee: p.amountNgwee,
    due_day: p.dueDay,
    effective_from: p.effectiveFrom,
    created_by: userId,
  })
  if (res.error) throw new DataError(res.error, 'Could not save the plan.')
}

export async function archivePlan(planId: string): Promise<void> {
  // An archived plan stops generating charges, but its open enrolments would
  // still look like "K50 every month" on screen while nobody is billed and
  // arrears quietly stop growing. So refuse while anyone is still on the plan.
  const open = await supabase.from('dues_enrollment').select('id').eq('plan_id', planId).is('end_on', null).limit(1)
  if (open.error) throw new DataError(open.error, 'Could not check who is on this plan.')
  if ((open.data ?? []).length > 0) {
    throw new DataError(
      { code: 'P0001', message: 'Some members are still on this plan. Stop their billing first, then remove the plan.' },
    )
  }
  const res = await supabase.from('dues_plan').update({ archived_at: new Date().toISOString() }).eq('id', planId)
  if (res.error) throw new DataError(res.error, 'Could not remove the plan.')
}

// ---------------------------------------------------------------- enrolments
export interface Enrollment {
  id: string
  membershipId: string
  memberId: string
  planId: string
  planName: string
  cadence: Cadence
  /** What this person is actually charged each period (override or the plan amount). */
  amountNgwee: number
  startOn: string
}

type EnrollmentRow = {
  id: string
  membership_id: string
  member_id: string
  plan_id: string
  start_on: string
  override_amount_ngwee: number | null
  plan: { name: string; cadence: Cadence; amount_ngwee: number } | null
}

// NOTE: dues_enrollment links to dues_plan through a TWO-column foreign key (plan_id +
// department_id), so PostgREST cannot resolve `plan:plan_id(...)` (PGRST200). Embed by TABLE
// name. The fake-server browser tests accepted the wrong form; only a real PostgREST caught it.
/** Current (not ended) enrolments in the department. */
export async function listEnrollments(departmentId: string): Promise<Enrollment[]> {
  const res = await supabase
    .from('dues_enrollment')
    .select('id, membership_id, member_id, plan_id, start_on, override_amount_ngwee, plan:dues_plan(name, cadence, amount_ngwee)')
    .eq('department_id', departmentId)
    .is('end_on', null)
  return (unwrap(res as never, 'Could not load who pays dues.') as EnrollmentRow[])
    .filter((r) => r.plan)
    .map((r) => ({
      id: r.id,
      membershipId: r.membership_id,
      memberId: r.member_id,
      planId: r.plan_id,
      planName: r.plan!.name,
      cadence: r.plan!.cadence,
      amountNgwee: Number(r.override_amount_ngwee ?? r.plan!.amount_ngwee),
      startOn: r.start_on,
    }))
}

export interface NewEnrollment {
  departmentId: string
  membershipId: string
  memberId: string
  planId: string
  /** First day of the first month to bill. Charges are created from here to today. */
  startOn: string
  overrideAmountNgwee?: number | null
}

/** First day of each month from `startOn` up to and including `today` (max 24). */
export function monthsToBill(startOn: string, today: string): string[] {
  const [sy, sm] = startOn.split('-').map(Number)
  const [ty, tm] = today.split('-').map(Number)
  const months: string[] = []
  let y = sy
  let m = sm
  while ((y < ty || (y === ty && m <= tm)) && months.length < 24) {
    months.push(`${y}-${String(m).padStart(2, '0')}-01`)
    m += 1
    if (m > 12) {
      m = 1
      y += 1
    }
  }
  return months
}

export async function enrolMember(e: NewEnrollment, today: string): Promise<void> {
  const userId = await currentUserId()
  const ins = await supabase.from('dues_enrollment').insert({
    department_id: e.departmentId,
    membership_id: e.membershipId,
    member_id: e.memberId,
    plan_id: e.planId,
    start_on: e.startOn,
    override_amount_ngwee: e.overrideAmountNgwee ?? null,
    created_by: userId,
  })
  // A unique violation means they are ALREADY on this plan, which is what a retry
  // looks like after a dropped connection part-way through the back-charges below.
  // Carry on and finish creating the charges (they are idempotent on the server).
  if (ins.error && ins.error.code !== '23505') throw new DataError(ins.error, 'Could not start their dues.')
  // Create the charges that are already due (idempotent on the server).
  for (const month of monthsToBill(e.startOn, today)) {
    await generateCharges(month, e.departmentId)
  }
}

export async function endEnrollment(enrollmentId: string, endOn: string): Promise<void> {
  const res = await supabase.from('dues_enrollment').update({ end_on: endOn }).eq('id', enrollmentId)
  if (res.error) throw new DataError(res.error, 'Could not stop their dues.')
}

export async function generateCharges(month: string, departmentId: string): Promise<void> {
  const res = await supabase.rpc('generate_dues_charges', { p_month: month, p_department_id: departmentId })
  if (res.error) throw new DataError(res.error, 'Could not create the dues charges.')
}

// ---------------------------------------------------------------- ledger & arrears
export interface ChargeLine {
  chargeId: string
  periodStart: string
  dueOn: string
  status: 'open' | 'settled' | 'waived' | 'cancelled'
  amountNgwee: number
  paidNgwee: number
  outstandingNgwee: number
  daysOverdue: number
}

type ChargeRow = {
  charge_id: string
  period_start: string
  due_on: string
  status: ChargeLine['status']
  amount_ngwee: number
  paid_ngwee: number
  outstanding_ngwee: number
  days_overdue: number
}

/** Every month a member has been billed, newest first. */
export async function memberLedger(departmentId: string, memberId: string): Promise<ChargeLine[]> {
  const res = await supabase
    .from('v_dues_charge_status')
    .select('charge_id, period_start, due_on, status, amount_ngwee, paid_ngwee, outstanding_ngwee, days_overdue')
    .eq('department_id', departmentId)
    .eq('member_id', memberId)
    .order('period_start', { ascending: false })
  return (unwrap(res as never, 'Could not load the payment history.') as ChargeRow[]).map((r) => ({
    chargeId: r.charge_id,
    periodStart: r.period_start,
    dueOn: r.due_on,
    status: r.status,
    amountNgwee: Number(r.amount_ngwee),
    paidNgwee: Number(r.paid_ngwee),
    outstandingNgwee: Number(r.outstanding_ngwee),
    daysOverdue: Number(r.days_overdue),
  }))
}

/** What a member still owes across all open charges (due now or not yet). */
export function totalOutstanding(ledger: ChargeLine[]): number {
  return ledger.filter((c) => c.status === 'open').reduce((sum, c) => sum + c.outstandingNgwee, 0)
}

export interface ArrearsEntry {
  memberId: string
  fullName: string
  phone: string | null
  periodsBehind: number
  arrearsNgwee: number
  oldestUnpaidPeriod: string
  maxDaysOverdue: number
}

type ArrearsRow = {
  member_id: string
  full_name: string
  phone: string | null
  periods_behind: number
  arrears_ngwee: number
  oldest_unpaid_period: string
  max_days_overdue: number
}

/** Everyone with something overdue, biggest debt first. */
export async function listArrears(departmentId: string): Promise<ArrearsEntry[]> {
  const res = await supabase
    .from('v_member_arrears')
    .select('member_id, full_name, phone, periods_behind, arrears_ngwee, oldest_unpaid_period, max_days_overdue')
    .eq('department_id', departmentId)
    .order('arrears_ngwee', { ascending: false })
  return (unwrap(res as never, 'Could not load who is behind on dues.') as ArrearsRow[]).map((r) => ({
    memberId: r.member_id,
    fullName: r.full_name,
    phone: r.phone,
    periodsBehind: Number(r.periods_behind),
    arrearsNgwee: Number(r.arrears_ngwee),
    oldestUnpaidPeriod: r.oldest_unpaid_period,
    maxDaysOverdue: Number(r.max_days_overdue),
  }))
}

// ---------------------------------------------------------------- payments
export interface Payment {
  id: string
  memberId: string | null
  memberName: string
  kind: 'dues' | 'project' | 'other'
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
  reference: string | null
  note: string | null
  status: 'draft' | 'posted' | 'reversed' | 'void'
  /** Set on the negative row that cancels another payment. */
  reversesId: string | null
  createdAt: string
}

type PaymentRow = {
  id: string
  member_id: string | null
  kind: Payment['kind']
  amount_ngwee: number
  method: PaymentMethod
  received_on: string
  reference: string | null
  note: string | null
  status: Payment['status']
  reverses_id: string | null
  created_at: string
  member: { full_name: string } | null
}

export interface PaymentFilter {
  departmentId: string
  /** 'YYYY-MM' to see one month only. */
  month?: string
  memberId?: string
  limit?: number
}

export async function listPayments(f: PaymentFilter): Promise<Payment[]> {
  let q = supabase
    .from('contribution')
    .select('id, member_id, kind, amount_ngwee, method, received_on, reference, note, status, reverses_id, created_at, member:member_id(full_name)')
    .eq('department_id', f.departmentId)
    .order('received_on', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(f.limit ?? 100)
  if (f.memberId) q = q.eq('member_id', f.memberId)
  if (f.month) {
    const [y, m] = f.month.split('-').map(Number)
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
    q = q.gte('received_on', `${f.month}-01`).lt('received_on', next)
  }
  const res = await q
  return (unwrap(res as never, 'Could not load the payments.') as PaymentRow[]).map((r) => ({
    id: r.id,
    memberId: r.member_id,
    memberName: r.member?.full_name ?? 'Unnamed',
    kind: r.kind,
    amountNgwee: Number(r.amount_ngwee),
    method: r.method,
    receivedOn: r.received_on,
    reference: r.reference,
    note: r.note,
    status: r.status,
    reversesId: r.reverses_id,
    createdAt: r.created_at,
  }))
}

export interface DuesPaymentInput {
  departmentId: string
  memberId: string
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
  reference?: string | null
  note?: string | null
  /** The collection sheet this cash was written on, if any. */
  sheetId?: string | null
  /**
   * Idempotency key: create ONE per form-open and reuse it on every retry. If the
   * first tap actually reached the server but the reply was lost, the retry
   * returns the original payment instead of recording the money twice.
   */
  clientRef: string
}

/** Record a payment and apply it to the member's oldest unpaid months. */
export async function recordDuesPayment(p: DuesPaymentInput): Promise<string> {
  if (!Number.isSafeInteger(p.amountNgwee) || p.amountNgwee <= 0) {
    throw new FriendlyError('Enter an amount greater than zero.')
  }
  const res = await supabase.rpc('record_dues_payment', {
    p_department_id: p.departmentId,
    p_member_id: p.memberId,
    p_amount_ngwee: p.amountNgwee,
    p_method: p.method,
    p_received_on: p.receivedOn,
    p_reference: p.reference?.trim() || null,
    p_sheet_id: p.sheetId ?? null,
    p_client_ref: p.clientRef,
    p_note: p.note?.trim() || null,
  })
  return unwrap(res as never, 'Could not record the payment.') as string
}

/** Cancel a payment with a visible reversal entry (the original is never deleted). */
export async function reversePayment(paymentId: string, reason: string): Promise<void> {
  const res = await supabase.rpc('reverse_contribution', { p_id: paymentId, p_reason: reason.trim() })
  if (res.error) throw new DataError(res.error, 'Could not reverse the payment.')
}
